import React, { useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid';
import lodash, { debounce } from 'lodash';

import * as SShared from '@/main/components/shared/styles'
import * as S from './styles';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import {
  FileCopy, Create as CreateIcon,
  Close as CloseIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { Controller, useForm } from 'react-hook-form';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import { Checkbox, FormControlLabel, TableCell, TableRow } from '@mui/material';
import BoxGrid from '@/main/components/shared/boxGrid/boxGrid';
import { KeyValue } from '@/main/models/common';
import { getPhoneCountryCode, getTranslateByKey, isNullUndefined } from '@/main/services/utils';
import { CityFilterRequest, MunicipalityFilterRequest, StateFilterRequest } from '@/main/models/request';
import { cityApi, countryInfoApi, municipalityApi, stateApi } from '@/main/api';
import { toast } from 'react-toastify';
import { Any } from 'typescript-compare';
import { Guid } from 'guid-typescript';

export interface IWarehouseInfoState {
  id: string;
  guid?: string;
  name: string;
  contactName: string;
  phoneNr: string;
  extension: string;
  zipCode: string;
  municipality: KeyValue;
  state: KeyValue;
  city: KeyValue;
  address: string;
  number: string | null;
  complement: string;
  codePhoneNumber: KeyValue | null;
  phoneIsWhatsApp: boolean;
}


const INITIAL_FORM_STATE = {
  id: '',
  name: '',
  contactName: '',
  phoneNr: '',
  extension: '',
  zipCode: '',
  municipality: null as KeyValue | null,
  state: null as KeyValue | null,
  city: null as KeyValue | null,
  address: '',
  number: '',
  noNumber: false,
  complement: '',
  codePhoneNumber: null as KeyValue | null,
  phoneIsWhatsApp: false,
}

type IWarehouseInfoForm = typeof INITIAL_FORM_STATE
interface IWarehouseInfoProps {
  dataWarehouse: IWarehouseInfoState[];
  watcherCountry?: KeyValue | null;
  setDataWarehouse: React.Dispatch<React.SetStateAction<IWarehouseInfoState[]>>
  handleCopyWarehouse: () => void;
  disableCopyWarehouse: boolean;
}

function WarehouseInfo({
  dataWarehouse,
  watcherCountry,
  setDataWarehouse,
  handleCopyWarehouse,
  disableCopyWarehouse
}: IWarehouseInfoProps) {
  // Hooks
  const {
    handleSubmit,
    control,
    clearErrors,
    getValues,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: INITIAL_FORM_STATE,
  })
  const watcherNoNumber = watch('noNumber')
  const watcherMunicipality = watch('municipality');
  const watcherCity = watch('city');

  // States
  const [hasEdit, setHasEdit] = useState(false)

  const [optionsMunicipality, setOptionsMunicipality] = useState<KeyValue[] | null>(null);
  const [loadingMunicipality, setLoadingMunicipality] = useState(false);
  const [optionsCity, setOptionsCity] = useState<KeyValue[] | null>(null);
  const [loadingCity, setLoadingCity] = useState(false);
  const [optionsState, setOptionsState] = useState<KeyValue[] | null>(null);
  const [loadingState, setLoadingState] = useState(false);

  // Methods & Variables for Form
  const isDisabledMunicipalityDrop = () =>
    optionsMunicipality === null
    || !watcherCountry
    || optionsMunicipality.length === 1;
  const isDisabledStateDrop = () =>
    optionsState === null
    || !watcherCountry
    || optionsState.length === 1;
  const isDisabledCityDrop = () =>
    optionsCity === null
    || !watcherCountry
    || optionsCity.length === 1;

  // Handle Functions
  function handleSubmitWarehouseInfo(data: IWarehouseInfoForm) {
    setDataWarehouse((prev) => {
      const newData = {
        ...data,
        number: data.noNumber ? null : data.number,
        id: uuidv4(),
      } as IWarehouseInfoState;

      reset(INITIAL_FORM_STATE)
      return [...prev, newData]
    });
  }
  function handleEditWarehouseInfo(warehouseEdit: IWarehouseInfoState) {
    setHasEdit(true);
    const hasNoNumber = warehouseEdit.number === undefined || warehouseEdit.number === '' || warehouseEdit.number === null;
    setValue('id', warehouseEdit.id);
    setValue('name', warehouseEdit.name);
    setValue('contactName', warehouseEdit.contactName);
    setValue('phoneNr', warehouseEdit.phoneNr);
    setValue('extension', warehouseEdit.extension);
    setValue('zipCode', warehouseEdit.zipCode);
    setValue('municipality', warehouseEdit.municipality);
    setValue('state', warehouseEdit.state);
    setValue('city', warehouseEdit.city);
    setValue('address', warehouseEdit.address);
    setValue('noNumber', hasNoNumber);
    setValue('number', hasNoNumber ? '' : String(warehouseEdit.number));
    setValue('complement', warehouseEdit.complement);
    setValue('codePhoneNumber', warehouseEdit.codePhoneNumber);
    setValue('phoneIsWhatsApp', warehouseEdit.phoneIsWhatsApp);

    loadOptionsMunicipality(watcherCountry?.key, warehouseEdit.zipCode)
    clearErrors();
  }
  function handleSubmitEditWarehouseInfo(data: IWarehouseInfoForm) {
    setDataWarehouse(prevState => {
      const clonePrevState = lodash.cloneDeep(prevState);
      const index = clonePrevState.findIndex(item => item.id === data.id);
      clonePrevState[index] = {
        ...data,
        number: data.noNumber ? null : data.number,
      } as IWarehouseInfoState;

      reset(INITIAL_FORM_STATE);
      setHasEdit(false);

      return [...clonePrevState];
    });
  }
  function handleDeleteWarehouseInfo(id: string) {
    setHasEdit(false);
    reset();
    setDataWarehouse(prevState => {
      const index = prevState.findIndex(item => item.id === id);
      prevState.splice(index, 1);
      return [...prevState];
    });
  }

  function loadInfoCountry(){
    if(isNullUndefined(watcherCountry)) return
    countryInfoApi
    .getCountryInfo(String(watcherCountry.key))
    .then((response) => {
       const {data: {result}} = response
       const codeCountry = getPhoneCountryCode()
       const findCodePhoneNumber = codeCountry.find(item => item.key === result.dialingCode) as KeyValue
       setValue('codePhoneNumber', findCodePhoneNumber)
    })
  }

  // Async Functions
  function callMunicipalityApiDomain(countrySelected: string | number | undefined | null, zipCode: string) {
    const MAX_LENGTH_ZIP_CODE = 5;
    setValue('municipality', null);
    setValue('state', null);
    setValue('city', null);

    setOptionsCity(null);
    setOptionsState(null);
    setOptionsMunicipality(null)

    if (countrySelected && zipCode && zipCode.length === MAX_LENGTH_ZIP_CODE) {
      const request = {
        guidCountry: String(countrySelected),
        zipCode: zipCode
      } as MunicipalityFilterRequest;

      setLoadingMunicipality(true);
      municipalityApi.getDomain(request)
        .then(res => {
          const result = res.data.result
          if (result.length === 0) {
            toast.error(getTranslateByKey('information.zipCodeNotFound', true));
            setValue('municipality', null)
            return;
          }
          if (result.length === 1) {
            clearErrors('municipality')
            setValue('municipality', result[0])
            return;
          }

          setValue('municipality', null)
          setOptionsMunicipality(result);
        })
        .catch(err => { })
        .finally(() => setLoadingMunicipality(false))

      // dispatch(getMunicipality(request))
    }
  }
  function callStatesApiDomain(citySelected: KeyValue | null | undefined) {
    setValue('state', null)

    if (citySelected !== null && citySelected !== undefined) {
      const request = {
        guidCity: String(citySelected.key || '')
      } as StateFilterRequest;

      setLoadingState(true);
      stateApi.getDomain(request)
        .then(res => {
          const result = res.data.result

          if (result.length === 0) {
            toast.error(getTranslateByKey('information.zipCodeNotFound'));
            setValue('state', null)
            return;
          }
          if (result.length === 1) {
            clearErrors('state')
            setValue('state', result[0])
            return;
          }

          setOptionsState(result);
        })
        .catch(err => { })
        .finally(() => setLoadingState(false))
    }
  }
  function callCityApiDomain(municipalitySelected: KeyValue | null | undefined) {
    setValue('state', null)

    if (municipalitySelected !== null && municipalitySelected !== undefined) {
      const request = {
        guidMunicipality: String(municipalitySelected.key || '')
      } as CityFilterRequest;

      setLoadingCity(true);
      cityApi.getDomain(request)
        .then(res => {
          const result = res.data.result
          if (result.length === 0) {
            toast.error(getTranslateByKey('information.zipCodeNotFound'));
            setValue('city', null)
            return;
          }
          if (result.length === 1) {
            clearErrors('city')
            setValue('city', result[0])
            return;
          }

          setOptionsCity(result);
        })
        .catch(err => { })
        .finally(() => setLoadingCity(false))
    }
  }

  function loadOptionsMunicipality(countrySelected: string | number | undefined | null, zipCode: string) {
    const request = {
      guidCountry: String(countrySelected),
      zipCode: zipCode
    } as MunicipalityFilterRequest;
    setLoadingMunicipality(true);

    municipalityApi.getDomain(request)
      .then(res => {
        const result = res.data.result
        setOptionsMunicipality(result)
      })
      .catch(err => { })
      .finally(() => setLoadingMunicipality(false))
  }

  useEffect(() => {
    const zipCodeForm = getValues('zipCode')
    callMunicipalityApiDomain(watcherCountry?.key, zipCodeForm)
  }, [watcherCountry]);

  useEffect(() => {
    callStatesApiDomain(watcherCity)
  }, [watcherCity]);

  useEffect(() => {
    callCityApiDomain(watcherMunicipality)
  }, [watcherMunicipality]);

  useEffect(() => {
    loadInfoCountry()
  }, [watcherCountry?.key])

  // Constants Layout
  const municipalityLabel = watcherCountry?.value === 'Mexico' ? `Municipality and ${getTranslateByKey('information.neighborhood').toLowerCase()}` : getTranslateByKey('information.neighborhood');

  const rowHeaders = [
    getTranslateByKey('information.warehouseName'),
    getTranslateByKey('information.contactName'),
    getTranslateByKey('information.phoneNr'),
    getTranslateByKey('information.extension'),
    getTranslateByKey('information.zipCode'),
    municipalityLabel,
    getTranslateByKey('information.state'),
    getTranslateByKey('information.city'),
    `${getTranslateByKey('information.address')} - ${getTranslateByKey('information.number')}`,
    getTranslateByKey('information.complement'),
    getTranslateByKey('information.actions')
  ];

  return (
    <S.Container id='id-register-warehouseInfo'>
      <S.HeaderWarehouse>
        <SShared.HeaderText>{getTranslateByKey('information.warehouse')}</SShared.HeaderText>
        <SecondaryButton
          type="button"
          disabled={disableCopyWarehouse}
          onClick={handleCopyWarehouse}
          style={{ padding: '.2rem .8rem', margin: '0 0 0 1rem' }}
          id='id-registerSupplier-button-copyAddress'>
          <FileCopy fontSize="small" />
          <span style={{ lineHeight: '0.7rem', marginLeft: '.4rem' }}>
            {getTranslateByKey('information.copyWarehouse')}
          </span>
        </SecondaryButton>
      </S.HeaderWarehouse>
      <form onSubmit={handleSubmit(hasEdit ? handleSubmitEditWarehouseInfo : handleSubmitWarehouseInfo)} noValidate>
        <S.AreaForm>
          <KogInputTextControlledNew
            name="name"
            id='id-registerSupplier-input-name'
            label={getTranslateByKey('information.warehouseName')}
            control={control}
            controllerProps={{ rules: { required: true, maxLength: 100 } }}
          />
          <KogInputTextControlledNew
            name="contactName"
            id='id-registerSupplier-input-contactName'
            label={getTranslateByKey('information.contactName')}
            control={control}
            controllerProps={{ rules: { required: true, maxLength: 200 } }}
          />

          <S.AreaInputNumber>
            <KogInputTextControlledNew
              hasOnlyNumber
              name="phoneNr"
              label={getTranslateByKey('information.phoneNr')}
              control={control}
              inputProps={{ maxLength: 12 }}
              controllerProps={{
                rules: { required: true, maxLength: 12, minLength: 10 }
              }}
              errorMessageCustom={
                errors.phoneNr?.type === 'required'
                  ? getTranslateByKey('information.phoneNrRequired', true)
                  : ''
              }
              id="id-register-txt-phoneNr-warehouseInfo"
              InputProps={{
                startAdornment: (
                  <KogAutoCompleteControlled
                  label={''}
                  nameCustom="codePhoneNumber"
                  control={control}
                  options={getPhoneCountryCode()}
                  isLoading={false}
                  hasErrorCustom={!!errors.codePhoneNumber}
                  onChangeCustom={() => {}}
                  id="id-register-txt-codePhoneNumber"
                  disableClearable={true}
                />
                ),
              }}
            />
              <Controller
                name="phoneIsWhatsApp"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <S.AreaCheckbox>
                    <FormControlLabel
                      label={getTranslateByKey('information.whatsappNumber')}
                      control={
                        <Checkbox
                          color="primary"
                          checked={value}
                          onChange={e => {
                            onChange(e.target.checked);
                          }}
                          id="id-register-checkBox-phoneIsWhatsApp"
                        />
                      }
                    />
                  </S.AreaCheckbox>
                )}
              />
          </S.AreaInputNumber>
          <KogInputTextControlledNew
            name="extension"
            id='id-registerSupplier-input-extension'
            label={getTranslateByKey('information.extension')}
            control={control}
            controllerProps={{ rules: { required: false, maxLength: 200 } }}
          />
          <KogInputTextControlledNew
            hasOnlyNumber
            name="zipCode"
            id='id-registerSupplier-input-zipCode'
            label={getTranslateByKey('information.zipCode')}
            control={control}
            inputProps={{ maxLength: 5 }}
            controllerProps={{ rules: { required: true, maxLength: 5 } }}
            onChangeCustom={debounce(e => {
              callMunicipalityApiDomain(watcherCountry?.key, e);
            }, 500)}
          />
          <KogAutoCompleteControlled
            nameCustom="municipality"
            id='id-registerSupplier-input-municipality'
            label={municipalityLabel}
            control={control}
            options={optionsMunicipality}
            isLoading={loadingMunicipality}
            rulesCustom={{ required: true }}
            disabled={isDisabledMunicipalityDrop()}
            onChangeCustom={() => { }}
          />
          <KogAutoCompleteControlled
            nameCustom="state"
            id='id-registerSupplier-input-state'
            label={getTranslateByKey('information.state')}
            control={control}
            options={optionsState}
            isLoading={loadingState}
            rulesCustom={{ required: true }}
            hasErrorCustom={!!errors.state}
            disabled={isDisabledStateDrop()}
            errorMessageCustom={
              errors.state?.type === 'required'
                ? getTranslateByKey('information.stateRequired', true)
                : ''
            }
            onChangeCustom={() => { }}
          />
          <KogAutoCompleteControlled
            nameCustom="city"
            id='id-registerSupplier-input-city'
            label={getTranslateByKey('information.city')}
            control={control}
            options={optionsCity}
            isLoading={loadingCity}
            rulesCustom={{ required: true }}
            hasErrorCustom={!!errors.city}
            disabled={isDisabledCityDrop()}
            errorMessageCustom={
              errors.city?.type === 'required'
                ? getTranslateByKey('information.cityRequired', true)
                : ''
            }
            onChangeCustom={() => { }}
          />

          <KogInputTextControlledNew
            name="address"
            id='id-registerSupplier-input-address'
            label={getTranslateByKey('information.address')}
            control={control}
            controllerProps={{ rules: { required: true, maxLength: 200 } }}
            errorMessageCustom={
              errors.address?.type === 'required'
                ? getTranslateByKey('information.addressRequired', true)
                : ''
            }
          />

          <S.AreaInputNumber>
            <KogInputTextControlledNew
              name="number"
              id='id-registerSupplier-input-number'
              label={getTranslateByKey('information.number')}
              disabled={watcherNoNumber}
              hasOnlyNumber
              control={control}
              controllerProps={{
                rules: {
                  required: !watcherNoNumber
                }
              }}
              inputProps={{ maxLength: 10 }}
            />
            <Controller
              name="noNumber"
              control={control}
              render={({
                field: { onChange, value }
              }) => (
                <S.AreaCheckbox>
                  <FormControlLabel
                    label={getTranslateByKey('information.noNumber')}
                    control={
                      <Checkbox
                        color="primary"
                        checked={value}
                        onChange={e => {
                          onChange(e.target.checked);
                          if (e.target.checked) {
                            clearErrors('number');
                            setValue('number', '');
                          }
                        }}
                        id='id-registerSupplier-checkbox-noNumber'
                      />
                    }
                  />
                </S.AreaCheckbox>
              )}
            />
          </S.AreaInputNumber>
          <KogInputTextControlledNew
            name="complement"
            id='id-registerSupplier-input-complement'
            label={getTranslateByKey('information.complement')}
            control={control}
            controllerProps={{ rules: { maxLength: 200 } }}
            inputProps={{ maxLength: 200 }}
          />

          <SecondaryButton
            id='id-registerSupplier-button-submit'
            type="submit"
            style={{ height: 'fit-content', padding: '.2rem .8rem', margin: '0.5rem 0 0 0' }}>
            {hasEdit
              ? getTranslateByKey('information.edit')
              : getTranslateByKey('information.add')}
            {` ${getTranslateByKey('information.warehouse').toLocaleLowerCase()}`}
          </SecondaryButton>
        </S.AreaForm>
      </form>
      {dataWarehouse.length > 0 && (
        <BoxGrid rowHeaders={rowHeaders}>
          {dataWarehouse.map(warehouse => (
            <TableRow key={warehouse.id}>
              <TableCell>{warehouse.name}</TableCell>
              <TableCell>{warehouse.contactName}</TableCell>
              <TableCell>
              <S.CollumnPhoneNumber>
              {`${warehouse.codePhoneNumber?.value} ${warehouse.phoneNr}`}
                {warehouse.phoneIsWhatsApp && (
                  <>
                   &nbsp;
                   <S.CustomColumn title={getTranslateByKey('information.whatsappMsg')}>
                      <WhatsAppIcon style={{color:'#25D366'}} fontSize='small'/>
                    </S.CustomColumn>
                  </>
                )}
              </S.CollumnPhoneNumber>
              </TableCell>
              <TableCell>{warehouse.extension}</TableCell>
              <TableCell>{warehouse.zipCode}</TableCell>
              <TableCell>{warehouse.municipality.value}</TableCell>
              <TableCell>{warehouse.state.value}</TableCell>
              <TableCell>{warehouse.city.value}</TableCell>
              <TableCell>{warehouse.address} {!!warehouse.number && ` - ${warehouse.number}`}</TableCell>
              <TableCell>{warehouse.complement}</TableCell>
              <TableCell width="5%">
                <CreateIcon
                  onClick={() => handleEditWarehouseInfo(warehouse)}
                  id={"id-register-icon-editWarehouse-" + (warehouse.name)}
                  className="cursor-pointer" />
                <CloseIcon
                  onClick={() => handleDeleteWarehouseInfo(warehouse.id)}
                  id={"id-register-icon-deleteWarehouse-" + (warehouse.name)}
                  className="cursor-pointer" />
              </TableCell>
            </TableRow>
          ))}
        </BoxGrid>
      )}
    </S.Container>
  )
}

export function formattedWarehouseGet(warehouse: any): IWarehouseInfoState {
  const [codePhone, phone] = warehouse.phone.split(" ");

  const codeCountry = getPhoneCountryCode()
  const findCodePhoneNumber = codeCountry.find(item => item.key === codePhone) ?? {key: '', value: ''}

  const warehouseFormatted : IWarehouseInfoState = {
      id: warehouse.guid,
      name: warehouse.name,
      contactName: warehouse.contactName,
      phoneNr: phone ?? warehouse.phone,
      extension: warehouse.extension,
      zipCode: warehouse.municipality.zipCode,
      municipality: {
        key: warehouse.municipality.guid,
        value: warehouse.municipality.name
      },
      state: {
        key: warehouse.municipality.city.state.guid,
        value: warehouse.municipality.city.state.name
      },
      city: {
        key: warehouse.municipality.city.guid,
        value: warehouse.municipality.city.name
      },
      address: warehouse.street,
      number: warehouse.number === '' ? null : warehouse.number,
      complement: warehouse.complement,
      phoneIsWhatsApp: warehouse.phoneIsWhatsApp,
      codePhoneNumber: findCodePhoneNumber,
  }
  return warehouseFormatted
  }

  export function formattedWarehouseSave(warehouses: IWarehouseInfoState[], isEditAddress = false){

    return warehouses.map(warehouse => ({
      ...(isEditAddress && {
        guid: Guid.isGuid(warehouse.id) ? warehouse.id : Guid.EMPTY
      }),
      name: warehouse.name,
      contactName: warehouse.contactName,
      street: warehouse.address,
      complement: warehouse.complement,
      number: warehouse.number,
      extension: warehouse.extension,
      phone: `${warehouse.codePhoneNumber?.key} ${warehouse.phoneNr}`,
      municipality: {
        guid: String(warehouse.municipality.key),
        zipCode: warehouse.zipCode
      },
      phoneIsWhatsApp: warehouse.phoneIsWhatsApp
    }))
}

export default WarehouseInfo;